import { loadTranslations } from '@/utils/translations';
import { ENVIRONMENT } from '@/environments/environment.ts';
import { IClientCulture } from '@/model/IClientCulture';
import { CultureAction } from '@/model/ICulture.ts';
import { getValidationErrorData, setError } from '@/lib/ErrorHandler.ts';
import { customJwtFetch, customFetch } from './customFetch';
import { IUser } from '@/model/IUser';

// BroadcastChannel para sincronização de tema entre abas/sites
let themeChannel: BroadcastChannel | null = null;

const initThemeChannel = () => {
  if (typeof BroadcastChannel !== 'undefined' && !themeChannel) {
    try {
      themeChannel = new BroadcastChannel('theme_sync');
      
      // Ouvir mudanças de tema de outras abas
      themeChannel.onmessage = (event) => {
        if (event.data.type === 'theme-change') {
          const newTheme = event.data.theme as 'light' | 'dark';
          localStorage.setItem('theme', newTheme);
          applyThemeToUI(newTheme);
          console.log(`Tema sincronizado para: ${newTheme}`);
        }
      };
    } catch (error) {
      console.warn('BroadcastChannel não suportado:', error);
    }
  }

  // Escutar mudanças no tema do sistema (navegador)
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    console.log('Tema do sistema mudou');
    updateButtonColors();
  });
};

const getCurrentTheme = (): 'light' | 'dark' => {
  // SEMPRE pegar do localStorage se existir
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme === 'light' || savedTheme === 'dark') {
    console.log(`Tema recuperado do localStorage: ${savedTheme}`);
    return savedTheme;
  }
  
  // Fallback apenas se não houver no localStorage
  const userPrefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const defaultTheme = userPrefersDark ? 'dark' : 'light';
  console.log(`Tema não encontrado no localStorage, usando preferência do sistema: ${defaultTheme}`);
  return defaultTheme;
};

const getSystemTheme = (): 'light' | 'dark' => {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getUserIdFromToken = async (): Promise<string | null> => {
  try {
    const token = await getAccessToken();
    if (token) {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.sub || decoded.userId || decoded.id || null;
    }
  } catch (error) {
    console.error('Erro ao decodificar token:', error);
  }
  return null;
};

const fetchUserPreference = async (): Promise<'light' | 'dark'> => {
  try {
    const response = await customJwtFetch<{ data: string; success: boolean; errors: any[] }>(ENVIRONMENT.authApiPath.user.userPreference);
    if (response.success && response.data) {
      const parsedData = JSON.parse(response.data);
      const theme = parsedData.theme;
      if (theme === 'light' || theme === 'dark') {
        localStorage.setItem('theme', theme);
        console.log(`Tema '${theme}' buscado da API e persistido no localStorage`);
        return theme;
      }
    }
  } catch (error) {
    console.error('Erro ao buscar preferência de tema da API:', error);
  }
  // Fallback para tema do sistema se a API falhar
  const fallbackTheme = getSystemTheme();
  localStorage.setItem('theme', fallbackTheme);
  return fallbackTheme;
};

const updateButtonColors = () => {
  const systemTheme = getSystemTheme();
  // Cores fixas com base no tema do sistema para máximo contraste
  const buttonColor = systemTheme === 'dark' ? '#ffffff' : '#000000';
  
  console.log(`🎨 Aplicando cor dos botões: ${buttonColor} (tema do sistema: ${systemTheme})`);
  
  const themeButton = document.getElementById('themeToggleButton') as HTMLButtonElement;
  if (themeButton) {
    themeButton.style.color = buttonColor;
    themeButton.style.setProperty('color', buttonColor, 'important');
    // Aplicar cor nos SVGs também
    const svg = themeButton.querySelector('svg');
    if (svg) {
      svg.style.color = buttonColor;
      svg.style.setProperty('color', buttonColor, 'important');
      svg.setAttribute('stroke', buttonColor);
      // Aplicar em todos os elementos do SVG
      const svgElements = svg.querySelectorAll('*');
      svgElements.forEach(el => {
        (el as SVGElement).style.color = buttonColor;
        (el as SVGElement).setAttribute('stroke', buttonColor);
      });
    }
    console.log('✓ Cor aplicada no botão de tema');
  } else {
    console.warn('⚠ Botão de tema não encontrado');
  }

  const clientButton = document.getElementById('ChangeClient') as HTMLAnchorElement;
  if (clientButton) {
    clientButton.style.color = buttonColor;
    clientButton.style.setProperty('color', buttonColor, 'important');
    console.log('✓ Cor aplicada no botão de cliente');
  } else {
    console.warn('⚠ Botão de cliente não encontrado');
  }

  // Aplicar cor no dropdown de idiomas e outros elementos do header
  const header = document.getElementById('ChromeHeader');
  if (header) {
    // Dropdown de idiomas (caption)
    const captionElements = header.querySelectorAll('p, a, button, span');
    captionElements.forEach(el => {
      const element = el as HTMLElement;
      // Não aplicar em elementos com classes específicas do OnePlace que já têm estilo próprio
      if (!element.classList.contains('op-') && !element.closest('.op-')) {
        element.style.color = buttonColor;
        element.style.setProperty('color', buttonColor, 'important');
      }
    });
    console.log('✓ Cor aplicada em elementos do header');
  }
};

const applyTheme = (theme: 'light' | 'dark') => {
  const root = window.document.documentElement;
  // Remover ambas as classes para evitar conflito
  root.classList.remove('light', 'dark');
  // Adicionar a classe do tema atual
  root.classList.add(theme);
  console.log(`🎨 Classe '${theme}' aplicada ao documentElement`);
};

// Função para aplicar o tema na UI
const applyThemeToUI = (theme: 'light' | 'dark') => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    document.documentElement.style.colorScheme = theme;
    
    // Atualizar o botão de tema se existir
    const button = document.getElementById('themeToggleButton') as HTMLButtonElement;
    if (button) {
        button.innerHTML = theme === 'dark' ? getSunIcon() : getMoonIcon();
    }
    
    // Atualizar as cores dos botões baseado no tema do sistema
    // Usar setTimeout para garantir que os elementos foram renderizados
    setTimeout(() => updateButtonColors(), 0);
};

const initializeThemeOnLoad = async () => {
  // Aguardar o DOM estar pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
      await applyStoredTheme();
    });
  } else {
    await applyStoredTheme();
  }
};

const applyStoredTheme = async () => {
  // Buscar tema da API antes de validar
  const theme = await fetchUserPreference();
  
  applyThemeToUI(theme);
  console.log(`✓ Tema '${theme}' aplicado após busca da API`);
};

const setTheme = (theme: 'light' | 'dark') => {
  applyThemeToUI(theme);
  localStorage.setItem('theme', theme);
  
  // Broadcast da mudança de tema para outras abas/aplicações
  if (themeChannel) {
    try {
      themeChannel.postMessage({ 
        type: 'theme-change', 
        theme,
        timestamp: Date.now()
      });
      console.log(`Tema alterado para: ${theme} e propagado para outras abas`);
    } catch (error) {
      console.warn('Erro ao broadcast de tema:', error);
    }
  }
};

const updateThemeButton = (theme: 'light' | 'dark') => {
  const button = document.getElementById('themeToggleButton') as HTMLButtonElement;
  if (button) {
    button.innerHTML = theme === 'dark' ? getSunIcon() : getMoonIcon();
  }
};


const getSunIcon = () => {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>`;
};

const getMoonIcon = () => {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path><circle cx="18" cy="6" r="0.5" fill="currentColor"></circle><circle cx="20" cy="9" r="0.5" fill="currentColor"></circle><circle cx="19" cy="4" r="0.5" fill="currentColor"></circle></svg>`;
};

const createThemeToggleButton = () => {
  const currentTheme = getCurrentTheme();
  
  const button = document.createElement('button');
  button.id = 'themeToggleButton';
  button.setAttribute('type', 'button');
  button.setAttribute('aria-label', 'Toggle theme');
  button.style.background = 'none';
  button.style.border = 'none';
  button.style.cursor = 'pointer';
  button.style.padding = '8px';
  button.style.display = 'inline-flex';
  button.style.alignItems = 'center';
  button.style.justifyContent = 'center';
  button.style.marginLeft = '8px';
  button.style.marginRight = '8px';
  button.style.fontSize = '18px';
  button.style.position = 'absolute';
  button.style.right = '350px';
  button.style.top = '50%';
  button.style.transform = 'translateY(-50%)';
  button.innerHTML = currentTheme === 'dark' ? getSunIcon() : getMoonIcon();
  
    button.addEventListener('click', async () => {
        const newTheme = getCurrentTheme() === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        button.innerHTML = newTheme === 'dark' ? getSunIcon() : getMoonIcon();
        
        // Enviar atualização para a API
        const userId = await getUserIdFromToken();
        if (userId) {
          try {
            await customJwtFetch(ENVIRONMENT.authApiPath.user.userPreference, {
              method: 'PUT',
              body: JSON.stringify({ userId, IsDarkMode: newTheme === 'dark' }),
              headers: { 'Content-Type': 'application/json' }
            });
            console.log(`Preferência de tema atualizada na API: ${newTheme}`);
          } catch (error) {
            console.error('Erro ao atualizar preferência de tema na API:', error);
          }
        }
    });
  
  return button;
};

export const applicationLoadStatus = {
  Error : 1,
  Loading : 2,
  Finished : 3,
  UserNotFound : 4,
  AccessRestricted : 5,
};

export const loadOnePlaceScript = async (setApplicationLoadStatusFinished) => {
  try {
    // Inicializar tema ao carregar a aplicação com um pequeno delay
    setTimeout(async () => {
      await initializeThemeOnLoad();
      initThemeChannel();
    }, 0);
    
    const onePlace = ENVIRONMENT.onePlace;

    if (!onePlace) {
      const errorData = getValidationErrorData('Environment variable \'oneplace\' not found');
      setError(errorData);
      setApplicationLoadStatusFinished(applicationLoadStatus.Error);
      console.error('[Error] OnePlace Script');
      return;
    }

    const node = document.createElement('script');
    node.src = onePlace;
    node.type = 'text/javascript';
    node.charset = 'utf-8';
    node.id = 'opHelper';

    node.onload = async function () {
      await customFetch<any>(`${ENVIRONMENT.baseUrlIcarus}/getVersion`).then(version => addBuildVersionIntoFooter(version));
      setApplicationLoadStatusFinished(applicationLoadStatus.Finished);
    };
    document.getElementsByTagName('head')[0].appendChild(node);
  } catch (e: any) {
    console.error(e);
    console.error('[Error] OnePlace Script');
    setApplicationLoadStatusFinished(applicationLoadStatus.Error);
  }
};

export const getOnePlace = () => {
  const onePlace = window['$OP' as string];

  return onePlace;
};

export const getAccessToken = async () => {
  const onePlace = getOnePlace();
  if (!!onePlace && typeof onePlace.loadChromeHeaderTool == 'function') {
    const token = await onePlace.getUserJwtRsaToken();
    if (token) {
      return token.token;
    }
  }
};

export const changeSignoutUrl = (shortName: string) => {
  const onePlace = getOnePlace();
  onePlace.signouturl = `/${shortName}`;
}

export const createHeaderInformation = async (cultures: IClientCulture[], selectedCulture: IClientCulture) => {
  const onePlace = getOnePlace();
  let _gObj: any = null;

  // Inicializar BroadcastChannel para sincronização de tema
  initThemeChannel();

  try {
    const languagedropdownitemclick = function (this: {
      name: string;
      value: string;
    }) {
      _gObj.setCaption(this.name);
      loadTranslations(Number(this.value));
    };

    const options = {
      captionRightAlignment: true,
      captionActions: (function () {
        const ret: CultureAction[] = [];
        cultures?.forEach((item) => {
          ret.push({
            name: item.name,
            value: item.id,
            action: languagedropdownitemclick,
          });
        });
        return ret;
      })(),

      apps: { autoEnableInUseAppList: true },
      usePrefersColorScheme: ENVIRONMENT.enableDarkMode ?? true,
      siteShortName: 'Data Validation System',
      header: { container: 'headerDomId' },
      footer: {
        enabled: true,
        container: 'footerDomId',
        supportInfo: {
          text: 'Support',
          email: 'oneplacesupport@willistowerswatson.com',
          action: function () {
            const link =
              'mailto:' +
              this.email +
              '?subject=OnePlace Support ' +
              document.title +
              '&body=PageLink:' +
              window.location.href;
            window.location.href = link;
          },
        },
      },
      signOut: {
        enabled: false,
        onSignOut: async () => {
          try {
            await customJwtFetch<{ accessToken: string } & IUser>(
              `${ENVIRONMENT.baseUrlIcarus}/Authorization/logout`,
              {
                method: 'POST',
                credentials: 'include',
              },
            );

          } catch (error) {
            console.error('Erro ao realizar logout da aplicação:', error);
          }
        },
      },
    };

    if (!document.getElementById('ChromeHeader')) {
      const obj = await onePlace.loadChromeHeaderTool(options);
      _gObj = obj;
      
      obj.setCaption(selectedCulture.name);

      // Adicione este bloco para garantir o redirecionamento do botão appicon
      setTimeout(() => {
        const appIcon = document.getElementById('appicon');
        if (appIcon) {
          appIcon.setAttribute('href', import.meta.env.VITE_DEADALUS);
          appIcon.onclick = (e) => {
            e.preventDefault();
            window.location.href = import.meta.env.VITE_DEADALUS;
          };
        }
      }, 0);

      // Usar MutationObserver para detectar quando o header é completamente carregado
      const observeHeader = () => {
        const headerDomId = document.getElementById('headerDomId');
        if (!headerDomId) {
          setTimeout(observeHeader, 100);
          return;
        }

        const observer = new MutationObserver(() => {
          if (!document.getElementById('themeToggleButton') && document.getElementById('ChromeHeader')) {
            addThemeButtonToHeader();
          }
        });

        observer.observe(headerDomId, {
          childList: true,
          subtree: true,
          attributes: true,
        });

        // Tentar adicionar o botão imediatamente
        addThemeButtonToHeader();

        // Parar de observar após encontrar o botão
        setTimeout(() => {
          if (document.getElementById('themeToggleButton')) {
            observer.disconnect();
          }
        }, 5000);
      };

      const addThemeButtonToHeader = () => {
        if (document.getElementById('themeToggleButton')) return;

        const header = document.getElementById('ChromeHeader');
        if (!header) return;

        // Garantir que o header tem position relative para o botão ser posicionado corretamente
        if (header.style.position !== 'absolute' && header.style.position !== 'fixed') {
          header.style.position = 'relative';
        }

        const themeButton = createThemeToggleButton();
        
        // Inserir o botão no final do header
        header.appendChild(themeButton);
        console.log('Tema toggle botão adicionado com sucesso no header');
        
        // Aplicar cores após adicionar ao DOM
        setTimeout(() => updateButtonColors(), 100);
      };

      observeHeader();
    }
  } catch (error) {
    console.error('Error loading header', error);
    throw error;
  }
};

export function addClientIntoHeader(text: string) {
  const intervalId = setInterval(() => {
    const header = document.getElementById('ChromeHeader');
    const headerDomId = document.getElementById('headerDomId');

    if (header && headerDomId) {
      clearInterval(intervalId);

      header.querySelector('a').href = import.meta.env.VITE_DEADALUS;
      const changeClient = document.getElementById('ChangeClient');

      if (!changeClient) {
        headerDomId.style.minWidth = '100vw';

        const clientName = document.createElement('a');
        clientName.textContent = text;
        clientName.id = 'ChangeClient';
        clientName.style.paddingLeft = '15px';
        clientName.style.marginLeft = '15px';
        clientName.style.marginRight = '24px';
        clientName.style.paddingTop = '8px';
        clientName.style.paddingBottom = '5px';
        clientName.style.fontSize = '16px';
        clientName.style.fontWeight = '500';

        const elemento2 = header.querySelector('p:nth-child(2)');

        if (window.innerWidth >= 768) {
          header.insertBefore(clientName, elemento2);
        }
      } else if (changeClient.textContent !== text) {
        changeClient.textContent = text;
      }
    }
  }, 500);
}

export function addBuildVersionIntoFooter(version: string) {
  const intervalId = setInterval(() => {
    const footer = document.getElementById('CommonFooter');

    if (footer && (ENVIRONMENT.buildVersion || version)) {
      clearInterval(intervalId);

      const existing = document.getElementById('BuildVersionText');
      if (existing) existing.remove();

      const p = document.createElement('span');
      p.id = 'BuildVersionText';
      p.innerText = `${version ? `Version: ${version}` : ''}${ENVIRONMENT.buildVersion ? `  Build: ${ENVIRONMENT.buildVersion}` : ''}`;
      p.style.float = 'right';
      p.style.marginTop = '17px';
      p.style.marginRight = '15px';
      p.style.fontSize = '12px';
      p.style.color = '#5c6066';
      footer.insertAdjacentElement('beforeend', p);
    }
  }, 2000);
}
